/**
 * Phase 13a — env-var parsing for the web shell.
 *
 * Vite exposes build-time vars under `import.meta.env`. The shell
 * reads three:
 *
 * - `VITE_CLERK_PUBLISHABLE_KEY` — Clerk publishable key for the
 *   client SDK. Required at deploy time; in dev the
 *   `developmentPlaceholder` fallback below keeps `pnpm dev`
 *   running with a clearly-named stub so the sign-in flow renders
 *   the Clerk widget against Clerk's dev instance rather than
 *   crashing on missing config.
 * - `VITE_API_BASE_URL` — the Hono+tRPC API origin. Defaults to
 *   `http://localhost:3000` for local dev (matching `apps/api`
 *   `server.ts`); production deploys set this to the Fly.io
 *   public URL.
 * - `VITE_ENABLE_SERVICE_WORKER` — `"true"` / `"false"`. The
 *   service worker registers in production builds and is opt-in
 *   in dev (the dev-server hot-reload story doesn't compose well
 *   with a stale cached SW; explicit opt-in keeps the dev loop
 *   tight). Defaults to `"false"` in dev, `"true"` in production.
 *
 * The parser is a pure function over a `Record<string, string |
 * undefined>` shape so unit tests don't need a Vite runtime to
 * exercise the branches. The runtime call site is
 * `parseEnv(import.meta.env)`.
 */

export type WebEnv = {
  readonly clerkPublishableKey: string;
  readonly clerkPublishableKeySource: "configured" | "development-placeholder";
  readonly apiBaseUrl: string;
  readonly enableServiceWorker: boolean;
  readonly mode: "development" | "production" | "test";
  /**
   * Phase 14c — crash-reporter DSN (Sentry / GlitchTip / Axiom-shaped).
   * Absent in dev / preview builds; the seam (`apps/web/src/seams/
   * crashReporter/`) falls back to its console-log stub when the
   * value is empty. `MVP.md` §6 DoD item 2 measures crash-free
   * session rate via this transport. Deploys MUST set
   * `VITE_CRASH_REPORTER_DSN`; the release-gate dashboard URL is
   * captured in `Progress.md` Phase 14c.
   */
  readonly crashReporterDsn: string | undefined;
  /**
   * Phase 14c — release identifier (build sha or version tag). The
   * crash reporter tags every event with this so the dashboard can
   * group events by release. Defaults to `"dev"` when
   * `VITE_RELEASE` is absent.
   */
  readonly release: string;
};

export type RawEnv = {
  readonly VITE_CLERK_PUBLISHABLE_KEY?: string | undefined;
  readonly VITE_API_BASE_URL?: string | undefined;
  readonly VITE_ENABLE_SERVICE_WORKER?: string | undefined;
  readonly VITE_CRASH_REPORTER_DSN?: string | undefined;
  readonly VITE_RELEASE?: string | undefined;
  readonly MODE?: string | undefined;
  readonly PROD?: boolean | undefined;
  readonly DEV?: boolean | undefined;
};

/**
 * Clearly-named dev placeholder. Real Clerk publishable keys are
 * prefixed `pk_test_` (development instance) or `pk_live_`
 * (production instance) and the suffix is a base64-encoded
 * frontend-API host terminated by `$` — the Clerk SDK *validates*
 * the shape at `<ClerkProvider>` mount and throws synchronously on a
 * malformed key, which would unmount the React tree.
 *
 * The suffix below decodes to `placeholder.clerk.invalid$`. The
 * `.invalid` TLD is reserved by RFC 2606 to never resolve, so Clerk's
 * async script load fails fast (auth doesn't actually work in
 * placeholder mode), but the SDK passes its boot-time validation and
 * the rest of the page renders. `clerkPublishableKeySource` flags this
 * mode so deploy scripts can still refuse to ship the placeholder.
 * Deploys MUST set `VITE_CLERK_PUBLISHABLE_KEY` for working auth.
 */
const DEV_CLERK_PLACEHOLDER =
  "pk_test_cGxhY2Vob2xkZXIuY2xlcmsuaW52YWxpZCQ";

const DEFAULT_API_BASE_URL = "http://localhost:8080";

export const parseEnv = (raw: RawEnv): WebEnv => {
  const mode = parseMode(raw);
  const isProduction = mode === "production";

  const configuredKey = nonEmpty(raw.VITE_CLERK_PUBLISHABLE_KEY);
  const clerkPublishableKey = configuredKey ?? DEV_CLERK_PLACEHOLDER;
  const clerkPublishableKeySource: WebEnv["clerkPublishableKeySource"] =
    configuredKey !== undefined ? "configured" : "development-placeholder";

  const apiBaseUrl = nonEmpty(raw.VITE_API_BASE_URL) ?? DEFAULT_API_BASE_URL;

  const enableServiceWorker = parseBoolean(
    raw.VITE_ENABLE_SERVICE_WORKER,
    isProduction
  );

  const crashReporterDsn = nonEmpty(raw.VITE_CRASH_REPORTER_DSN);
  const release = nonEmpty(raw.VITE_RELEASE) ?? "dev";

  return {
    clerkPublishableKey,
    clerkPublishableKeySource,
    apiBaseUrl,
    enableServiceWorker,
    mode,
    crashReporterDsn,
    release,
  };
};

const parseMode = (raw: RawEnv): WebEnv["mode"] => {
  const explicit = nonEmpty(raw.MODE);
  if (explicit === "production" || explicit === "test") {
    return explicit;
  }
  if (raw.PROD === true) return "production";
  return "development";
};

const parseBoolean = (raw: string | undefined, fallback: boolean): boolean => {
  const value = nonEmpty(raw);
  if (value === undefined) return fallback;
  if (value === "true" || value === "1") return true;
  if (value === "false" || value === "0") return false;
  return fallback;
};

const nonEmpty = (raw: string | undefined): string | undefined => {
  if (raw === undefined) return undefined;
  const trimmed = raw.trim();
  return trimmed.length === 0 ? undefined : trimmed;
};
